/**
 * The one overdue-wait control (#746 1.8): Chase, Re-plan, Snooze. It is driven by `overdue_waiting_party` and drawn
 * on the queue row (hover icons and ⋯ menu), Peek, the case header strip and My day, so no surface carries its own
 * copy. For a technician the first action is "Ask tech for update" (a private-note card a manager confirms), never a
 * customer email. Chasing here only opens a draft or a card; nothing is sent from this control.
 */
import { Button, MenuItem, MenuSeparator } from '@patchbay/ui'
import { AlarmClock, ListTodo, MessageSquareMore } from 'lucide-react'
import type * as React from 'react'

export type OverdueParty = 'customer' | 'contractor' | 'vendor' | 'tech'

/** The case-view action id a chase on an external party opens. A technician is asked through a card, not chased. */
export function chaseActionId(party: Exclude<OverdueParty, 'tech'>): 'chase_customer' | 'chase_external' {
  return party === 'customer' ? 'chase_customer' : 'chase_external'
}

export const chaseLabel = (party: OverdueParty) => (party === 'tech' ? 'Follow up' : 'Chase')

export interface OverdueHandlers {
  onChase?: () => void
  onReplan?: () => void
  onSnooze?: () => void
}

export function OverdueActions({
  party,
  variant,
  onChase,
  onReplan,
  onSnooze,
  onClick,
}: OverdueHandlers & {
  party: OverdueParty
  /** `icons`: row hover toolbar. `buttons`: Peek, case header, My day. `menu`: ⋯ menu items. */
  variant: 'icons' | 'buttons' | 'menu'
  /** Wraps each handler, e.g. to stop a row click from also selecting the row. */
  onClick?: (handler: () => void) => React.MouseEventHandler
}) {
  const wrap = (handler: () => void): React.MouseEventHandler => (onClick ? onClick(handler) : () => handler())
  const chase = chaseLabel(party)
  if (variant === 'menu') {
    return (
      <>
        {onChase ? (
          <MenuItem onClick={onChase}>
            <MessageSquareMore /> {chase}
          </MenuItem>
        ) : null}
        {onReplan ? (
          <MenuItem onClick={onReplan} keys="n">
            <ListTodo /> Re-plan
          </MenuItem>
        ) : null}
        {onSnooze ? (
          <MenuItem onClick={onSnooze} keys="s">
            <AlarmClock /> Snooze
          </MenuItem>
        ) : null}
        <MenuSeparator />
      </>
    )
  }
  if (variant === 'icons') {
    return (
      <>
        {onChase ? (
          <Button size="icon-sm" variant="ghost" aria-label={chase} title={`${chase} (overdue wait)`} onClick={wrap(onChase)}>
            <MessageSquareMore />
          </Button>
        ) : null}
        {onReplan ? (
          <Button size="icon-sm" variant="ghost" aria-label="Re-plan" title="Re-plan overdue wait (n)" onClick={wrap(onReplan)}>
            <ListTodo />
          </Button>
        ) : null}
        {onSnooze ? (
          <Button size="icon-sm" variant="ghost" aria-label="Snooze" title="Snooze overdue wait (s)" onClick={wrap(onSnooze)}>
            <AlarmClock />
          </Button>
        ) : null}
      </>
    )
  }
  return (
    <div role="toolbar" aria-label="Overdue wait actions" className="flex flex-wrap gap-1.5">
      {onChase ? (
        <Button size="sm" variant="primary" onClick={wrap(onChase)}>
          <MessageSquareMore /> {chase}
        </Button>
      ) : null}
      {onReplan ? (
        <Button size="sm" variant="outline" onClick={wrap(onReplan)}>
          <ListTodo /> Re-plan
        </Button>
      ) : null}
      {onSnooze ? (
        <Button size="sm" variant="outline" onClick={wrap(onSnooze)}>
          <AlarmClock /> Snooze
        </Button>
      ) : null}
    </div>
  )
}
